<template>
  <div class="modal-inner">
    <!-- Fejléc -->
    <div class="modal-head">
      <h2 class="modal-title">{{ isEdit ? 'Edit employee' : 'New employee' }}</h2>
      <div class="modal-head-right">
        <div class="status-wrap">
          <span class="form-label" style="margin:0;">Status:</span>
          <select
            :value="formData.status"
            class="status-select"
            :class="isActiveStatus ? 'status-select--active' : 'status-select--inactive'"
            @change="onStatusChange($event.target.value)"
          >
            <option :value="1">Active</option>
            <option :value="0">Inactive</option>
          </select>
        </div>
        <button class="btn btn-secondary btn-sm" @click="$emit('cancel')">✕</button>
      </div>
    </div>

    <!-- Tabok -->
    <div class="modal-tabs">
      <button
        v-for="tab in visibleTabs"
        :key="tab.key"
        class="tab-btn"
        :class="{
          'tab-btn--active': activeTab === tab.key,
          'tab-btn--admin':  tab.key === 'admin'
        }"
        @click="activeTab = tab.key"
      >{{ tab.icon }} {{ tab.label }}</button>
    </div>

    <!-- Mezők -->
    <div class="modal-body">

      <!-- Admin tab egyedi UI -->
      <template v-if="activeTab === 'admin'">
        <div class="admin-intro">
          <span class="admin-intro-icon">⚙️</span>
          <div>
            <strong>Salary calculation settings</strong>
            <p>These values are used to calculate the employee's monthly compensation. Set per employee.</p>
          </div>
        </div>
        <div class="admin-grid">
          <div class="admin-card">
            <div class="admin-card-header">
              <span class="admin-card-icon">⛽</span>
              <span class="admin-card-label">Fuel allowance (Ft/day)</span>
            </div>
            <div class="admin-card-input-wrap">
              <input
                class="admin-card-input"
                type="number"
                min="0"
                step="1"
                :value="formData.fuel_allowance || ''"
                placeholder="e.g. 500"
                @input="$emit('update-field', 'fuel_allowance', $event.target.value ? Number($event.target.value) : null)"
              />
              <span class="admin-card-unit">Ft</span>
            </div>
            <p class="admin-card-hint">Attendance days × this rate</p>
          </div>

          <div class="admin-card">
            <div class="admin-card-header">
              <span class="admin-card-icon">⏱️</span>
              <span class="admin-card-label">Hourly rate (Ft/hour)</span>
            </div>
            <div class="admin-card-input-wrap">
              <input
                class="admin-card-input"
                type="number"
                min="0"
                step="1"
                :value="formData.hourly_rate || ''"
                placeholder="e.g. 2500"
                @input="$emit('update-field', 'hourly_rate', $event.target.value ? Number($event.target.value) : null)"
              />
              <span class="admin-card-unit">Ft</span>
            </div>
            <p class="admin-card-hint">Worked hours × this rate</p>
          </div>

          <div class="admin-card">
            <div class="admin-card-header">
              <span class="admin-card-icon">🏖️</span>
              <span class="admin-card-label">Vacation day rate (Ft/day)</span>
            </div>
            <div class="admin-card-input-wrap">
              <input
                class="admin-card-input"
                type="number"
                min="0"
                step="1"
                :value="formData.vacation_rate || ''"
                placeholder="e.g. 10000"
                @input="$emit('update-field', 'vacation_rate', $event.target.value ? Number($event.target.value) : null)"
              />
              <span class="admin-card-unit">Ft</span>
            </div>
            <p class="admin-card-hint">Vacation days × this rate</p>
          </div>

          <div class="admin-card">
            <div class="admin-card-header">
              <span class="admin-card-icon">💵</span>
              <span class="admin-card-label">Base salary (Ft/month)</span>
            </div>
            <div class="admin-card-input-wrap">
              <input
                class="admin-card-input"
                type="number"
                min="0"
                step="1"
                :value="formData.base_salary || ''"
                placeholder="e.g. 350000"
                @input="$emit('update-field', 'base_salary', $event.target.value ? Number($event.target.value) : null)"
              />
              <span class="admin-card-unit">Ft</span>
            </div>
            <p class="admin-card-hint">Fixed monthly base amount</p>
          </div>
        </div>
      </template>

      <!-- Normál tab mezők -->
      <div v-if="activeTab !== 'admin'" class="form-grid">
        <div
          v-for="field in currentFields"
          :key="field.key"
          class="form-field"
          :class="{ 'form-field--full': field.type === 'textarea' }"
        >
          <label class="form-label">{{ field.label }}</label>

          <!-- Relation select -->
          <select
            v-if="field.type === 'relation'"
            class="form-input"
            :value="getRelationId(field.key)"
            @change="onRelationChange(field, $event.target.value)"
          >
            <option value="">— Select —</option>
            <option
              v-for="item in getCollection(field.collection)"
              :key="item.id"
              :value="item.id"
            >{{ item.name }}</option>
          </select>

          <!-- Plain select -->
          <select
            v-else-if="field.type === 'select'"
            class="form-input"
            :value="getVal(field.path)"
            @change="$emit('update-field', field.path, $event.target.value)"
          >
            <option value="">— Select —</option>
            <option v-for="opt in field.options" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <!-- Textarea -->
          <textarea
            v-else-if="field.type === 'textarea'"
            class="form-input"
            rows="4"
            :value="getVal(field.path)"
            @input="$emit('update-field', field.path, $event.target.value)"
            style="resize:vertical;"
          ></textarea>

          <!-- Toggle -->
          <div v-else-if="field.type === 'toggle'" class="toggle-wrap">
            <button
              type="button"
              class="toggle-btn"
              :class="getVal(field.path) == 1 ? 'toggle-btn--on' : 'toggle-btn--off'"
              @click="$emit('update-field', field.path, getVal(field.path) == 1 ? 0 : 1)"
            >
              <span class="toggle-knob"></span>
            </button>
            <span class="toggle-label">{{ getVal(field.path) == 1 ? 'Yes' : 'No' }}</span>
          </div>

          <!-- Default input -->
          <input
            v-else
            class="form-input"
            :type="field.type"
            :value="getVal(field.path)"
            @input="$emit('update-field', field.path, $event.target.value)"
          />
        </div>
      </div>

      <!-- Attachments (csak Documents fülön) -->
      <template v-if="activeTab === 'documents'">
        <div class="attach-divider">
          <span class="attach-divider-label">📎 Attachments</span>
        </div>

        <div v-if="existingAttachments.length" class="attach-list">
          <div
            v-for="(file, idx) in existingAttachments"
            :key="'existing-' + (file.id || idx)"
            class="attach-item"
          >
            <div v-if="isImage(file.mime_type)" class="attach-thumb">
              <img :src="base_url + '/' + file.path" :alt="file.filename" />
            </div>
            <span v-else class="attach-icon">{{ fileIcon(file.filename, file.mime_type) }}</span>
            <div class="attach-info">
              <span class="attach-name">{{ file.filename }}</span>
              <span class="attach-size">{{ formatSize(file.size) }} · {{ file.mime_type }}</span>
            </div>
            <a
              target="_blank"
              class="attach-action attach-action--download"
              title="Download"
              :href="base_url + '/' + file.path"
            >👁️</a>
            <button
              class="attach-action attach-action--delete"
              title="Remove"
              @click="askDeleteExisting(idx, file)"
            >🗑️</button>
          </div>
        </div>

        <div v-if="newFiles.length" class="attach-list">
          <div
            v-for="(file, idx) in newFiles"
            :key="'new-' + idx"
            class="attach-item attach-item--new"
          >
            <div v-if="file.type && file.type.startsWith('image/')" class="attach-thumb">
              <img :src="getObjectUrl(file)" :alt="file.name" />
            </div>
            <span v-else class="attach-icon">{{ fileIcon(file.name, file.type) }}</span>
            <div class="attach-info">
              <span class="attach-name">{{ file.name }}</span>
              <span class="attach-size">{{ formatSize(file.size) }} · {{ file.type }}</span>
            </div>
            <span class="attach-badge">New</span>
            <button class="attach-action attach-action--delete" @click="removeNew(idx)">✕</button>
          </div>
        </div>

        <div
          class="attach-dropzone"
          :class="{ 'attach-dropzone--over': dragOver }"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
          @click="$refs.fileInput.click()"
        >
          <div class="attach-dropzone-inner">
            <span class="attach-dropzone-icon">📂</span>
            <p class="attach-dropzone-text">Drop files here or <strong>click to browse</strong></p>
            <p class="attach-dropzone-hint">Any file type accepted</p>
          </div>
          <input ref="fileInput" type="file" multiple style="display:none" @change="onFileInput" />
        </div>
      </template>
    </div>

    <!-- Lábléc -->
    <div class="modal-foot">
      <button class="btn btn-secondary" @click="$emit('cancel')" :disabled="saving">Cancel</button>
      <button class="btn btn-primary" @click="$emit('save')" :disabled="saving">
        {{ saving ? '⏳ Saving...' : '💾 Save' }}
      </button>
    </div>

    <!-- Attachment delete confirm snackbar -->
    <transition name="snack">
      <div v-if="confirmDeleteAttachment" class="attach-confirm-snack">
        <span class="attach-confirm-icon">🗑️</span>
        <div class="attach-confirm-text">
          <strong>Delete attachment?</strong>
          <span>{{ confirmDeleteAttachment.file.filename }}</span>
        </div>
        <div class="attach-confirm-actions">
          <button
            class="btn-snack btn-snack--cancel"
            @click="cancelDeleteExisting"
            :disabled="deletingAttachment"
          >Cancel</button>
          <button
            class="btn-snack btn-snack--confirm"
            @click="confirmDeleteExisting"
            :disabled="deletingAttachment"
          >{{ deletingAttachment ? '⏳' : 'Delete' }}</button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const FIELD_DEFS = [
  // Personal
  { key: 'surname',              label: 'Last name',           path: 'surname',              type: 'text',     group: 'personal'   },
  { key: 'christian_name1',      label: 'First name',          path: 'christian_name1',      type: 'text',     group: 'personal'   },
  { key: 'christian_name2',      label: 'Middle name',         path: 'christian_name2',      type: 'text',     group: 'personal'   },
  { key: 'email',                label: 'Email',               path: 'email',                type: 'email',    group: 'personal'   },
  { key: 'birth_name',           label: 'Birth name',          path: 'birth_name',           type: 'text',     group: 'personal'   },
  { key: 'mother_name',          label: 'Mother name',         path: 'mother_name',          type: 'text',     group: 'personal'   },
  { key: 'birth_place',          label: 'Birth place',         path: 'birth_place',          type: 'text',     group: 'personal'   },
  { key: 'birth_date',           label: 'Birth date',          path: 'birth_date',           type: 'date',     group: 'personal'   },
  // Documents
  { key: 'identity_card_number', label: 'Identity card no.',   path: 'identity_card_number', type: 'text',     group: 'documents'  },
  { key: 'address_card_number',  label: 'Address card no.',    path: 'address_card_number',  type: 'text',     group: 'documents'  },
  { key: 'vat_number',           label: 'Tax identifier',      path: 'vat_number',           type: 'text',     group: 'documents'  },
  { key: 'insurance_number',     label: 'Social Sec. no.',     path: 'insurance_number',     type: 'text',     group: 'documents'  },
  { key: 'medical_end',     label: 'Medical end',     path: 'medical_end',     type: 'date',     group: 'documents'  },
  { key: 'fire_safety_end',     label: 'Fire safety end',     path: 'fire_safety_end',     type: 'date',     group: 'documents'  },
  // Employment
  { key: 'position',   label: 'Position',   path: 'position_id',   type: 'relation', group: 'employment', collection: 'positions'   },
  { key: 'department', label: 'Department', path: 'department_id', type: 'relation', group: 'employment', collection: 'departments' },
  { key: 'site',       label: 'Site',       path: 'site_id',       type: 'relation', group: 'employment', collection: 'sites'       },
  { key: 'education',  label: 'Education',  path: 'education_id',  type: 'relation', group: 'employment', collection: 'educations'  },
  { key: 'work_start', label: 'Start of employment', path: 'work_start', type: 'date', group: 'employment' },
  { key: 'work_end',   label: 'End of employment',   path: 'work_end',   type: 'date', group: 'employment' },
  // Other
  { key: 'comment',    label: 'Comment',    path: 'comment', type: 'textarea', group: 'other' },
  { key: 'locale',     label: 'Locale',     path: 'locale',  type: 'select',   group: 'other', options: ['hu', 'en', 'ro', 'sl'] },
  { key: 'login',      label: 'Login',      path: 'login',      type: 'toggle', group: 'other' },
  { key: 'portal',     label: 'Portal',     path: 'portal',     type: 'toggle', group: 'other' },
  { key: 'newsletter', label: 'Newsletter', path: 'newsletter', type: 'toggle', group: 'other' },
];

const TAB_GROUPS = [
  { key: 'personal',   label: 'Personal data', icon: '👤', adminOnly: false },
  { key: 'documents',  label: 'Documents',     icon: '📋', adminOnly: false },
  { key: 'employment', label: 'Employment',    icon: '🏢', adminOnly: false },
  { key: 'other',      label: 'Other',         icon: '💬', adminOnly: false },
  { key: 'admin',      label: 'Admin',         icon: '⚙️', adminOnly: true  },
];

import ApiService from '@/core/services/api.service';

const ATTACHMENT_ENDPOINT = 'humanResources/employee';

export default {
  name: 'EmployeeForm',

  props: {
    formData:    { type: Object,  required: true },
    isEdit:      { type: Boolean, default: false },
    saving:      { type: Boolean, default: false },
    positions:   { type: Array,   default: function() { return []; } },
    departments: { type: Array,   default: function() { return []; } },
    sites:       { type: Array,   default: function() { return []; } },
    educations:  { type: Array,   default: function() { return []; } },
    permissions: { type: Array,   default: function() { return []; } },
  },

  data() {
    return {
      activeTab:               'personal',
      tabGroups:               TAB_GROUPS,
      fieldDefs:               FIELD_DEFS,
      newFiles:                [],
      dragOver:                false,
      confirmDeleteAttachment: null,
      deletingAttachment:      false,
      base_url:                process.env.VUE_APP_BACKEND_URL,
    };
  },

  computed: {
    isAdmin() {
      return this.permissionCan('admin');
    },

    visibleTabs() {
      var self = this;
      return this.tabGroups.filter(function(t) {
        return !t.adminOnly || self.isAdmin;
      });
    },

    currentFields() {
      var tab = this.activeTab;
      return this.fieldDefs.filter(function(f) { return f.group === tab; });
    },

    isActiveStatus() {
      var s = this.formData.status;
      return s === 1 || s === '1' || s === 'active';
    },

    existingAttachments() {
      var a = this.formData.employee_documents;
      if (!a) return [];
      if (Array.isArray(a)) return a;
      if (a.data && Array.isArray(a.data)) return a.data;
      return [];
    },
  },

  watch: {
    'formData.id': {
      handler: function() {
        this.activeTab = 'personal';
        this.newFiles  = [];
      },
    },
  },

  methods: {
    // ── Permission ───────────────────────────────

    permissionCan: function(action) {
      var isPermission = this.permissions.find(function(x) {
        return x.name === 'humanResources.employee.' + action;
      });
      return isPermission ? true : false;
    },

    // ── Field helpers ────────────────────────────

    getVal: function(path) {
      var result = path.split('.').reduce(function(o, k) {
        return o != null ? o[k] : undefined;
      }, this.formData);
      return result != null ? result : '';
    },

    getRelationId: function(key) {
      var idKey = key + '_id';
      if (this.formData[idKey] != null) return this.formData[idKey];
      if (this.formData[key] && this.formData[key].id != null) return this.formData[key].id;
      return '';
    },

    getCollection: function(name) {
      var map = {
        positions:   this.positions,
        departments: this.departments,
        sites:       this.sites,
        educations:  this.educations,
      };
      return map[name] || [];
    },

    onRelationChange: function(field, idStr) {
      var id         = idStr ? Number(idStr) : null;
      var collection = this.getCollection(field.collection);
      var found      = collection.find(function(i) { return i.id === id; }) || null;
      this.$emit('update-field', field.key, found);
    },

    onStatusChange: function(value) {
      this.$emit('update-field', 'status', value === '1' ? 1 : 0);
    },

    // ── Attachments ──────────────────────────────

    onDrop: function(e) {
      this.dragOver = false;
      this.addFiles(Array.from(e.dataTransfer.files));
    },

    onFileInput: function(e) {
      this.addFiles(Array.from(e.target.files));
      e.target.value = '';
    },

    addFiles: function(files) {
      var self = this;
      files.forEach(function(f) {
        var exists = self.newFiles.some(function(n) {
          return n.name === f.name && n.size === f.size;
        });
        if (!exists) self.newFiles.push(f);
      });
      this.$emit('files-changed', this.newFiles);
    },

    removeNew: function(idx) {
      this.newFiles.splice(idx, 1);
      this.$emit('files-changed', this.newFiles);
    },

    askDeleteExisting: function(idx, file) {
      this.confirmDeleteAttachment = { idx: idx, file: file };
    },

    cancelDeleteExisting: function() {
      this.confirmDeleteAttachment = null;
    },

    confirmDeleteExisting: function() {
      var self = this;
      var idx  = this.confirmDeleteAttachment.idx;
      var file = this.confirmDeleteAttachment.file;
      this.deletingAttachment = true;

      ApiService.delete(ATTACHMENT_ENDPOINT + '/' + this.formData.id + '/attachments/' + file.id)
        .then(function() {
          var updated = self.existingAttachments.slice();
          updated.splice(idx, 1);
          self.$emit('update-field', 'attachments', updated);
          self.confirmDeleteAttachment = null;
        })
        .catch(function(e) {
          console.error('Attachment delete failed:', e);
        })
        .finally(function() {
          self.deletingAttachment = false;
        });
    },

    // ── File helpers ─────────────────────────────

    getObjectUrl: function(file) {
      return URL.createObjectURL(file);
    },

    isImage: function(mimeType) {
      return mimeType && mimeType.indexOf('image/') === 0;
    },

    fileIcon: function(filename, mimeType) {
      if (mimeType) {
        if (mimeType.indexOf('image/') === 0)    return '🖼️';
        if (mimeType === 'application/pdf')       return '📕';
        if (mimeType.indexOf('word') !== -1)      return '📘';
        if (mimeType.indexOf('excel') !== -1 || mimeType.indexOf('spreadsheet') !== -1) return '📗';
        if (mimeType.indexOf('zip') !== -1 || mimeType.indexOf('rar') !== -1)           return '🗜️';
        if (mimeType.indexOf('text/') === 0)      return '📝';
      }
      if (filename) {
        var ext = filename.split('.').pop().toLowerCase();
        var map = { pdf: '📕', doc: '📘', docx: '📘', xls: '📗', xlsx: '📗', ppt: '📙', pptx: '📙', zip: '🗜️', rar: '🗜️', txt: '📝', csv: '📊' };
        return map[ext] || '📄';
      }
      return '📄';
    },

    formatSize: function(bytes) {
      if (!bytes) return '';
      if (bytes < 1024)        return bytes + ' B';
      if (bytes < 1048576)     return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / 1048576).toFixed(1) + ' MB';
    },
  },
};
</script>

<style scoped>
.modal-inner  { display: flex; flex-direction: column; max-height: 90vh; position: relative; }
.modal-head   { padding: 20px 24px 16px; border-bottom: 1px solid #F0F4FA; display: flex; justify-content: space-between; align-items: center; }
.modal-title  { font-family: 'Playfair Display', serif; font-size: 20px; font-weight: 600; margin: 0; }
.modal-head-right { display: flex; align-items: center; gap: 12px; }
.modal-tabs   { display: flex; gap: 6px; padding: 12px 24px 0; border-bottom: 1px solid #F0F4FA; }
.tab-btn--admin { margin-left: auto; }
.modal-body   { flex: 1; overflow-y: auto; padding: 24px; }
.modal-foot   { padding: 16px 24px; border-top: 1px solid #F0F4FA; display: flex; justify-content: flex-end; gap: 10px; }

.status-wrap   { display: flex; align-items: center; gap: 8px; }
.status-select { padding: 5px 10px; border: 1.5px solid #E2E6F0; border-radius: 8px; font-family: inherit; font-size: 13px; font-weight: 600; outline: none; cursor: pointer; background: #fff; }
.status-select--active   { color: #1A9E5C; border-color: #1A9E5C; }
.status-select--inactive { color: #E05252; border-color: #E05252; }

.tab-btn { padding: 8px 16px; border: none; background: none; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; color: #7B82A0; border-radius: 8px 8px 0 0; transition: all .15s; display: flex; align-items: center; gap: 6px; border-bottom: 2px solid transparent; margin-bottom: -1px; }
.tab-btn:hover:not(.tab-btn--active) { background: #EEF1FF; color: #5B6BFF; }
.tab-btn--active { background: #5B6BFF; color: #fff; }

.form-grid        { display: grid; grid-template-columns: repeat(2, 1fr); gap: 18px; }
.form-field       { display: flex; flex-direction: column; gap: 5px; }
.form-field--full { grid-column: 1 / -1; }
.form-label       { font-size: 11px; font-weight: 600; color: #7B82A0; text-transform: uppercase; letter-spacing: .06em; }
.form-input       { padding: 10px 14px; border: 1.5px solid #E2E6F0; border-radius: 10px; font-size: 14px; font-family: inherit; color: #1a1a2e; background: #FAFBFF; transition: border-color .15s, box-shadow .15s; outline: none; }
.form-input:focus { border-color: #5B6BFF; box-shadow: 0 0 0 3px rgba(91,107,255,.12); background: #fff; }

.btn           { cursor: pointer; border: none; font-family: inherit; font-weight: 600; border-radius: 10px; transition: filter .15s, transform .15s; display: inline-flex; align-items: center; gap: 6px; }
.btn:hover     { filter: brightness(.92); transform: translateY(-1px); }
.btn-primary   { background: linear-gradient(135deg, #5B6BFF, #8B5BFF); color: #fff; padding: 10px 24px; font-size: 14px; }
.btn-secondary { background: #F0F4FA; color: #4A5068; padding: 8px 14px; font-size: 13px; }
.btn-sm        { padding: 6px 12px !important; font-size: 12px !important; }

/* ── Toggle ── */
.toggle-wrap  { display: flex; align-items: center; gap: 10px; padding: 6px 0; }
.toggle-btn   { position: relative; width: 44px; height: 24px; border-radius: 12px; border: none; cursor: pointer; transition: background .2s; flex-shrink: 0; padding: 0; }
.toggle-btn--on  { background: #1A9E5C; }
.toggle-btn--off { background: #D1D5E0; }
.toggle-knob  { position: absolute; top: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.2); transition: left .2s; }
.toggle-btn--on  .toggle-knob { left: 23px; }
.toggle-btn--off .toggle-knob { left: 3px; }
.toggle-label { font-size: 13px; font-weight: 600; color: #4A5068; }

/* ── Attachments ── */
.attach-divider       { display: flex; align-items: center; gap: 10px; margin: 24px 0 14px; }
.attach-divider::before, .attach-divider::after { content: ''; flex: 1; height: 1px; background: #E8EBF5; }
.attach-divider-label { font-size: 11px; font-weight: 700; color: #7B82A0; text-transform: uppercase; letter-spacing: .08em; white-space: nowrap; }
.attach-list      { display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }
.attach-item      { display: flex; align-items: center; gap: 10px; padding: 9px 12px; background: #F8F9FD; border: 1px solid #E8EBF5; border-radius: 10px; }
.attach-item--new { background: #EEF8F2; border-color: #B6E8CC; }
.attach-thumb     { width: 40px; height: 40px; border-radius: 6px; overflow: hidden; flex-shrink: 0; background: #E8EBF5; }
.attach-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.attach-icon      { font-size: 20px; flex-shrink: 0; width: 40px; text-align: center; }
.attach-info      { flex: 1; min-width: 0; }
.attach-name      { display: block; font-size: 13px; font-weight: 600; color: #1a1a2e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.attach-size      { font-size: 11px; color: #9DA5C0; }
.attach-badge     { font-size: 10px; font-weight: 700; background: #1A9E5C; color: #fff; padding: 2px 7px; border-radius: 20px; flex-shrink: 0; }
.attach-action    { cursor: pointer; border: none; background: none; font-size: 15px; padding: 4px; border-radius: 6px; flex-shrink: 0; transition: background .15s; line-height: 1; text-decoration: none; }
.attach-action:hover { background: #E8EBF5; }
.attach-action--download { color: #5B6BFF; }
.attach-action--delete   { color: #E05252; }
.attach-dropzone        { border: 2px dashed #C5CAD8; border-radius: 12px; padding: 28px 20px; text-align: center; cursor: pointer; transition: border-color .15s, background .15s; margin-top: 4px; }
.attach-dropzone:hover,
.attach-dropzone--over  { border-color: #5B6BFF; background: #F0F2FF; }
.attach-dropzone-inner  { pointer-events: none; }
.attach-dropzone-icon   { font-size: 32px; display: block; margin-bottom: 8px; }
.attach-dropzone-text   { font-size: 14px; color: #4A5068; margin: 0 0 4px; }
.attach-dropzone-hint   { font-size: 12px; color: #9DA5C0; margin: 0; }

/* ── Attachment delete confirm snackbar ── */
.attach-confirm-snack { position: absolute; bottom: 80px; left: 50%; transform: translateX(-50%); background: #1a1a2e; color: #fff; border-radius: 14px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; box-shadow: 0 8px 32px rgba(0,0,0,.25); z-index: 10; min-width: 360px; max-width: 90%; white-space: nowrap; }
.attach-confirm-icon { font-size: 18px; flex-shrink: 0; }
.attach-confirm-text { display: flex; flex-direction: column; gap: 1px; flex: 1; min-width: 0; }
.attach-confirm-text strong { font-size: 13px; font-weight: 700; }
.attach-confirm-text span   { font-size: 12px; color: #9DA5C0; overflow: hidden; text-overflow: ellipsis; }
.attach-confirm-actions     { display: flex; gap: 8px; flex-shrink: 0; }
.btn-snack            { cursor: pointer; border: none; border-radius: 8px; font-family: inherit; font-size: 12px; font-weight: 700; padding: 6px 14px; transition: filter .15s; }
.btn-snack:disabled   { opacity: .5; cursor: not-allowed; }
.btn-snack--cancel    { background: rgba(255,255,255,.12); color: #fff; }
.btn-snack--cancel:hover  { filter: brightness(1.2); }
.btn-snack--confirm   { background: #E05252; color: #fff; }
.btn-snack--confirm:hover { filter: brightness(1.15); }
.snack-enter-active { animation: snackIn .2s cubic-bezier(.16,1,.3,1); }
.snack-leave-active { animation: snackIn .15s cubic-bezier(.16,1,.3,1) reverse; }
@keyframes snackIn  { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

/* ── Admin tab ── */
.admin-intro      { display: flex; align-items: flex-start; gap: 12px; background: #FFF8E6; border: 1px solid #F5D87A; border-radius: 12px; padding: 14px 16px; margin-bottom: 20px; }
.admin-intro-icon { font-size: 22px; flex-shrink: 0; margin-top: 1px; }
.admin-intro strong { display: block; font-size: 13px; font-weight: 700; color: #92620A; margin-bottom: 2px; }
.admin-intro p    { font-size: 12px; color: #A07830; margin: 0; line-height: 1.5; }
.admin-grid       { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
.admin-card       { background: #F8F9FD; border: 1.5px solid #E8EBF5; border-radius: 14px; padding: 16px 18px; transition: border-color .15s; }
.admin-card:focus-within { border-color: #5B6BFF; background: #fff; }
.admin-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.admin-card-icon   { font-size: 20px; }
.admin-card-label  { font-size: 11px; font-weight: 700; color: #7B82A0; text-transform: uppercase; letter-spacing: .06em; }
.admin-card-input-wrap { display: flex; align-items: center; border: 1.5px solid #E2E6F0; border-radius: 10px; background: #fff; overflow: hidden; margin-bottom: 8px; transition: border-color .15s, box-shadow .15s; }
.admin-card:focus-within .admin-card-input-wrap { border-color: #5B6BFF; box-shadow: 0 0 0 3px rgba(91,107,255,.12); }
.admin-card-input  { flex: 1; border: none; outline: none; padding: 10px 14px; font-size: 18px; font-weight: 700; color: #1a1a2e; font-family: inherit; background: transparent; width: 0; }
.admin-card-input::placeholder { color: #C5CAD8; font-weight: 400; font-size: 16px; }
.admin-card-unit   { padding: 0 14px; font-size: 13px; font-weight: 600; color: #9DA5C0; border-left: 1px solid #E8EBF5; display: flex; align-items: center; background: #F8F9FD; align-self: stretch; }
.admin-card-hint   { font-size: 11px; color: #9DA5C0; margin: 0; }
</style>